import { preparePayment, createCase } from './../utils/apiCall'
import { getOrCreateIdempotencyKey, clearIdempotencyKey } from './idempotency'

/**
 * Custom error class for payment errors
 */
export class PaymentError extends Error {
  constructor(message, type, details = {}) {
    super(message)
    this.name = 'PaymentError'
    this.type = type // 'STRIPE_ERROR', 'API_ERROR', 'DUPLICATE_PAYMENT', 'VALIDATION_ERROR'
    this.details = details
  }
}

export const handleCardPayment = async (payload, stripe, cardElement) => {
  const idempotencyKey = getOrCreateIdempotencyKey(payload.ProspectId)

  try {
    // Step 1: Prepare payment intent
    const { resource } = await preparePayment(payload)
    if (!resource) {
      throw new PaymentError('Failed to prepare payment', 'API_ERROR', {
        step: 'prepare_payment'
      })
    }

    // Step 2: Confirm card payment with Stripe
    const { id, error } = await confirmPayment(stripe, resource, cardElement)
    if (error) {
      throw new PaymentError(error.message || 'Card payment confirmation failed', 'STRIPE_ERROR', {
        step: 'confirm_payment',
        stripeError: error
      })
    }

    if (!id) {
      throw new PaymentError('No payment intent ID returned', 'STRIPE_ERROR', {
        step: 'confirm_payment'
      })
    }

    // Step 3: Create case with idempotency key
    payload.PaymentSource = id
    try {
      const { status, certificateUrl } = await createCase(payload, idempotencyKey)

      if (status === 'subscribed') {
        // Success - clear idempotency key
        clearIdempotencyKey(payload.ProspectId)
        return certificateUrl
      } else {
        throw new PaymentError('Case creation returned unexpected status', 'API_ERROR', {
          step: 'create_case',
          status
        })
      }
    } catch (error) {
      // Handle specific HTTP errors
      if (error.status === 409) {
        throw new PaymentError(
          'This payment has already been processed',
          'DUPLICATE_PAYMENT',
          {
            step: 'create_case',
            caseId: error.data?.caseId,
            originalError: error
          }
        )
      }

      // Re-throw if already a PaymentError
      if (error instanceof PaymentError) {
        throw error
      }

      // Wrap other errors
      throw new PaymentError(
        error.data?.message || error.message || 'Failed to create case',
        'API_ERROR',
        {
          step: 'create_case',
          originalError: error
        }
      )
    }
  } catch (error) {
    // Re-throw PaymentError as-is
    if (error instanceof PaymentError) {
      throw error
    }

    // Wrap unexpected errors
    throw new PaymentError(
      error.message || 'Unexpected error during payment',
      'API_ERROR',
      { originalError: error }
    )
  }
}

export const prepareBancontactPayment = async (payload) => {
  try {
    const { resource } = await preparePayment(payload)
    if (!resource) {
      throw new PaymentError('Failed to prepare Bancontact payment', 'API_ERROR', {
        step: 'prepare_payment'
      })
    }

    // Redirect to Bancontact
    window.location.href = resource
  } catch (error) {
    if (error instanceof PaymentError) {
      throw error
    }
    throw new PaymentError(
      error.message || 'Failed to initiate Bancontact payment',
      'API_ERROR',
      { originalError: error }
    )
  }
}

export const proceedBancontactPayment = async (payload) => {
  const idempotencyKey = getOrCreateIdempotencyKey(payload.ProspectId)

  try {
    const { status, certificateUrl } = await createCase(payload, idempotencyKey)

    if (status === 'subscribed') {
      // Success - clear idempotency key
      clearIdempotencyKey(payload.ProspectId)
      return certificateUrl
    } else {
      throw new PaymentError('Case creation returned unexpected status', 'API_ERROR', {
        step: 'create_case',
        status
      })
    }
  } catch (error) {
    // Handle specific HTTP errors
    if (error.status === 409) {
      throw new PaymentError(
        'This payment has already been processed',
        'DUPLICATE_PAYMENT',
        {
          step: 'create_case',
          caseId: error.data?.caseId,
          originalError: error
        }
      )
    }

    // Re-throw if already a PaymentError
    if (error instanceof PaymentError) {
      throw error
    }

    // Wrap other errors
    throw new PaymentError(
      error.data?.message || error.message || 'Failed to create case',
      'API_ERROR',
      {
        step: 'create_case',
        originalError: error
      }
    )
  }
}

const confirmPayment = async (stripe, clientSecret, cardElement) => {
  const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
    payment_method: { card: cardElement }
  })

  return {
    id: paymentIntent?.id,
    error: error
  }
}
